import React, { useEffect, useRef, useState } from "react";
import { Music, Pause, Phone, Volume2, VolumeX, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useSite } from "../context/SiteContext";

const STORAGE_KEY = "letgo:music-pref";
// Music plays on every public page except admin / editor surfaces and the
// printable marketing kit (where audio would interfere with screenshots).
const BLOCKED_ROUTES = ["/admin", "/marketing"];
// Fallback music — used when no URL has been set in the admin editor.
const FALLBACK_MUSIC_URL =
  "https://customer-assets.emergentagent.com/job_wellness-hub-1420/artifacts/mzms5gso_My%20Song%206.m4a";
const FALLBACK_MUSIC_LABEL = "Let Go";

// Horizontal sticky music banner — sits right under the main navigation.
export default function BackgroundMusic() {
  const { site } = useSite();
  const location = useLocation();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.3);
  const [muted, setMuted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showVolume, setShowVolume] = useState(false);

  const src = site?.background_music_url || FALLBACK_MUSIC_URL;
  const label = site?.background_music_label || FALLBACK_MUSIC_LABEL;
  const phone = site?.phone || "203-437-8193";
  const telHref = `tel:${phone.replace(/[^\d+]/g, "")}`;

  // Restore prefs. Volume & mute persist across visits (localStorage); the
  // dismiss flag only lives for the current tab/session so the banner comes
  // back on every fresh visit.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (typeof p.volume === "number") setVolume(p.volume);
        if (typeof p.muted === "boolean") setMuted(p.muted);
        // Legacy migration — older versions stored `dismissed` in localStorage,
        // which kept the banner hidden forever. Strip it out so returning
        // visitors get the banner back.
        if (typeof p.dismissed === "boolean") {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ volume: p.volume, muted: p.muted }));
        }
      }
      const sessionDismissed = sessionStorage.getItem("letgo:music-dismissed");
      if (sessionDismissed === "1") setDismissed(true);
    } catch (_) { /* ignore */ }
  }, []);

  // Persist prefs
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ volume, muted }));
      if (dismissed) {
        sessionStorage.setItem("letgo:music-dismissed", "1");
      } else {
        sessionStorage.removeItem("letgo:music-dismissed");
      }
    } catch (_) { /* ignore */ }
  }, [volume, muted, dismissed]);

  // Apply volume / muted to the actual element
  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = volume;
    audioRef.current.muted = muted;
  }, [volume, muted]);

  // Pause when navigating to blocked surfaces.
  const isBlocked = BLOCKED_ROUTES.some((p) => location.pathname.startsWith(p));
  useEffect(() => {
    if (isBlocked && audioRef.current) {
      audioRef.current.pause();
      setPlaying(false);
    }
  }, [isBlocked]);

  if (!src || dismissed || isBlocked) {
    // Make sure the navbar snaps back to top when the banner isn't shown.
    if (typeof document !== "undefined") {
      document.documentElement.style.removeProperty("--music-banner-h");
    }
    return null;
  }

  // Tell the navbar (and any other top-fixed element) how tall the banner is.
  if (typeof document !== "undefined") {
    document.documentElement.style.setProperty("--music-banner-h", "44px");
  }

  const toggle = async () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      try {
        await el.play();
        setPlaying(true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Music play failed", e);
      }
    }
  };

  return (
    <>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={src}
        loop
        preload="none"
        data-testid="background-music-audio"
      />

      {/* Horizontal banner — sits above the Navbar at the very top of the page. */}
      <div
        data-testid="background-music-banner"
        className={`fixed top-0 left-0 right-0 z-[60] bg-[#2E4A33] text-[#FDFBF7] shadow-md ${!playing ? "lgw-music-banner-pulse" : ""}`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2.5 flex items-center justify-between gap-4">
          {/* Left — label */}
          <div className="flex items-center gap-3 min-w-0">
            <Music className="w-4 h-4 shrink-0 text-[#E8C669]" />
            <p className="text-[11px] sm:text-xs uppercase tracking-[0.32em] font-medium truncate">
              {playing ? `Now playing · ${label}` : `Play soft music while you browse`}
            </p>
          </div>

          {/* Right — controls */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Volume control popover */}
            <div className="relative flex items-center">
              <button
                type="button"
                onClick={() => setShowVolume((v) => !v)}
                data-testid="background-music-volume-toggle"
                aria-label="Volume"
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                {muted || volume === 0 ? (
                  <VolumeX className="w-4 h-4" />
                ) : (
                  <Volume2 className="w-4 h-4" />
                )}
              </button>
              {showVolume && (
                <div
                  className="absolute top-full right-0 mt-2 bg-[#FDFBF7] text-[#1C1917] border border-[#E2DCD0] rounded-sm shadow-xl p-3 flex items-center gap-2"
                  style={{ width: "200px" }}
                  data-testid="background-music-volume-popover"
                >
                  <button
                    type="button"
                    onClick={() => setMuted(!muted)}
                    className="text-[#3A5A40] hover:text-[#2E4A33] transition-colors"
                    aria-label={muted ? "Unmute" : "Mute"}
                  >
                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={volume}
                    onChange={(e) => { setVolume(Number(e.target.value)); if (muted) setMuted(false); }}
                    className="flex-1 accent-[#3A5A40]"
                    data-testid="background-music-volume"
                    aria-label="Volume"
                  />
                </div>
              )}
            </div>

            {/* Play / Pause button */}
            <button
              type="button"
              onClick={toggle}
              data-testid="background-music-toggle"
              className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#E8C669] text-[#1C1917] hover:bg-[#F2D77A] rounded-full text-[10px] sm:text-xs uppercase tracking-[0.28em] font-medium transition-colors"
            >
              {playing ? <Pause className="w-3.5 h-3.5" /> : <Music className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{playing ? "Pause" : "Play"}</span>
            </button>

            {/* Call us — quick-call button */}
            <a
              href={telHref}
              data-testid="banner-call-us"
              aria-label={`Call ${phone}`}
              className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#FDFBF7] text-[#1C1917] hover:bg-[#EAE5D9] rounded-full text-[10px] sm:text-xs uppercase tracking-[0.28em] font-medium transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Call us</span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
